import { Button, MenuItem, Select, TextField } from '@material-ui/core';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { ModalWrapper } from 'components/ModalWrapper';
import { useAiAssistant } from '../../AiAssistantContext';
import * as api from '../../api';
import { AVAILABLE_MODELS, ISession } from '../../types';
import InlineConfirmDelete from '../shared/InlineConfirmDelete';

interface IProps {
  session: ISession;
  onClose: () => void;
  onSave: (session: ISession) => void;
  onDelete: (sessionId: number) => void;
}

const SessionSettingsModal: React.FC<IProps> = ({ session, onClose, onSave, onDelete }) => {
  const { state } = useAiAssistant();
  const [title, setTitle] = useState(session.title);
  const [model, setModel] = useState(session.model);
  const [projectId, setProjectId] = useState(session.projectId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await api.updateSession(session.id, { title, model, projectId });
      if (result.ok && result.data) {
        onSave(result.data);
      } else {
        setError(result.message || 'Failed to save');
      }
    } catch {
      setError('Failed to save session settings');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.deleteSession(session.id);
      onDelete(session.id);
    } catch {
      setError('Failed to delete session');
    }
  };

  const cancelDelete = useCallback(() => setConfirmingDelete(false), []);

  return (
    <ModalWrapper onClose={onClose} title={`Session Settings: ${session.title}`} width={450} height={440}>
      <Form>
        {error && <ErrorText>{error}</ErrorText>}
        <TextField
          label="Title"
          variant="outlined"
          size="small"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
        />
        <SelectWrapper>
          <SelectLabel>Project</SelectLabel>
          <Select
            variant="outlined"
            value={projectId}
            onChange={(e) => setProjectId(Number(e.target.value))}
            fullWidth
            style={{ fontSize: 14 }}
          >
            {state.projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </SelectWrapper>
        <SelectWrapper>
          <SelectLabel>Model</SelectLabel>
          <Select
            variant="outlined"
            value={model}
            onChange={(e) => setModel(e.target.value as string)}
            fullWidth
            style={{ fontSize: 14 }}
          >
            {AVAILABLE_MODELS.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                <span>{m.label}</span>
                <ModelHint>{m.hint}</ModelHint>
              </MenuItem>
            ))}
          </Select>
        </SelectWrapper>
        {confirmingDelete && (
          <InlineConfirmDelete
            itemName={session.title}
            onConfirm={handleDelete}
            onCancel={cancelDelete}
          />
        )}
        <ButtonRow>
          {!confirmingDelete && (
            <DeleteButton variant="outlined" onClick={() => setConfirmingDelete(true)}>
              Delete
            </DeleteButton>
          )}
          <Spacer />
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ButtonRow>
      </Form>
    </ModalWrapper>
  );
};

export default SessionSettingsModal;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SelectLabel = styled.label`
  font-size: 12px;
  color: #666;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const DeleteButton = styled(Button)`
  && {
    color: #e53935;
    border-color: #e53935;
  }
`;

const ModelHint = styled.span`
  font-size: 11px;
  color: #999;
  margin-left: 8px;
`;

const ErrorText = styled.div`
  color: #e53935;
  font-size: 13px;
`;
